import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { ErrorView } from '../api/queryView'

/**
 * Связь с сервером глазами оболочки (решение владельца 23.10.2026): одна на всё приложение, а не своя у каждого экрана.
 *
 * - `online` — сервер отвечает;
 * - `reconnecting` — запрос не дошёл до сервера (сеть, таймаут, прокси без ответа службы): оболочка проверяет доступность
 *   сама с растущей паузой;
 * - `offline` — браузер сообщил, что сети на устройстве нет. Проверки сервера идут и тут (RP-SALE-FAST, К-111): событие `online`
 *   браузер присылает не всегда, а первый же ответ сервера возвращает связь независимо от него.
 *
 * Срок жизни — `screen`: после F5 связь проверяется заново первым же запросом.
 */
export type ConnectionStatus = 'online' | 'reconnecting' | 'offline'

export interface ConnectionState {
  status: ConnectionStatus
  /** С какого момента (мс) связи нет; `null` — связь есть. */
  since: number | null
  /** Сколько проверок доступности уже назначено с момента потери связи. */
  attempts: number
  /** Последняя ошибка связи — для строки «для администратора». */
  lastError: ErrorView | null
  /** Когда (мс) пойдёт следующая проверка; `null` — не назначена (идёт сейчас или связь есть). */
  nextCheckAt: number | null
  /**
   * Когда (мс) сервер ответил в последний раз — любой итог запроса, где он отозвался (`serverAnswered`). Возраст данных на экране
   * (RP-SALE-FAST, К-110) считается от него, а не от итога запроса: пока запрос висит, возраст растёт. После обрыва остаётся
   * моментом последнего ответа до обрыва — для «Последний ответ» в мини-окне «Связь» (RP-UI-LINK). `null` — сервер ещё ни разу не
   * ответил с открытия страницы.
   */
  lastOkAt: number | null
  /** Сколько неудачных попыток достучаться подряд с момента потери связи (запросы и проверки монитора). */
  failures: number
  /** Сколько действий человека (изменяющих запросов — «Оплатить», запись) сейчас ждут ответа. */
  userPending: number
  /** Действие человека оборвалось без ответа за эту потерю связи: исход неизвестен, сказать об этом нужно. */
  userFailed: boolean
}

export const CONNECTION_ONLINE: ConnectionState = {
  status: 'online',
  since: null,
  attempts: 0,
  lastError: null,
  nextCheckAt: null,
  lastOkAt: null,
  failures: 0,
  userPending: 0,
  userFailed: false,
}

const connectionSlice = createSlice({
  name: 'connection',
  initialState: CONNECTION_ONLINE,
  reducers: {
    /** Запрос не дошёл до сервера. Счётчик попыток не трогаем: его ведёт монитор проверок; `lastOkAt` остаётся моментом прошлого ответа. */
    connectionLost(state, action: PayloadAction<{ error: ErrorView; at: number }>) {
      state.lastError = action.payload.error
      state.failures += 1
      if (state.status === 'online') {
        state.status = 'reconnecting'
        state.since = action.payload.at
      }
    },
    /** Сервер ответил на запрос (любой HTTP-ответ): момент ответа запоминается. Статус не меняется — выход из обрыва это `connectionRestored`. */
    serverAnswered(state, action: PayloadAction<{ at: number }>) {
      state.lastOkAt = action.payload.at
    },
    /** Сервер ответил после обрыва: всё сбрасывается, кроме момента последнего ответа. */
    connectionRestored(state) {
      return { ...CONNECTION_ONLINE, lastOkAt: state.lastOkAt, userPending: state.userPending }
    },
    /** Началось действие человека (изменяющий запрос). */
    userActionStarted(state) {
      state.userPending += 1
    },
    /** Действие закончилось; `reached: false` — ответа от сервера нет. */
    userActionEnded(state, action: PayloadAction<{ reached: boolean }>) {
      state.userPending = Math.max(0, state.userPending - 1)
      if (!action.payload.reached) state.userFailed = true
    },
    /** Браузер сообщил, что сети нет: окно говорит «нет интернета»; проверки сервера идут дальше с теми же паузами. */
    browserWentOffline(state, action: PayloadAction<{ at: number }>) {
      state.status = 'offline'
      state.since ??= action.payload.at
      state.nextCheckAt = null
    },
    /** Сеть на устройстве появилась: сразу проверить сервер, а не считать связь восстановленной. */
    browserWentOnline(state) {
      if (state.status !== 'offline') return
      state.status = 'reconnecting'
      state.nextCheckAt = null
    },
    /** Монитор назначил очередную проверку доступности (пока связи нет — и при `reconnecting`, и при `offline`). */
    connectionCheckScheduled(state, action: PayloadAction<{ at: number; attempt: number }>) {
      if (state.status === 'online') return
      state.nextCheckAt = action.payload.at
      state.attempts = action.payload.attempt
    },
  },
})

export const {
  connectionLost,
  serverAnswered,
  connectionRestored,
  userActionStarted,
  userActionEnded,
  browserWentOffline,
  browserWentOnline,
  connectionCheckScheduled,
} = connectionSlice.actions
export const connectionReducer = connectionSlice.reducer

export const selectConnectionStatus = (state: ConnectionState): ConnectionStatus => state.status
/**
 * Нужно ли сказать человеку о потере связи окном. Только когда сбой подтверждён серией (не меньше `CONNECTION_MIN_FAILURES`
 * неудач подряд; единичный и секундный обрыв проходят незаметно) И человек ждёт ответа на своё действие либо оно оборвалось.
 * Без действия человека потеря остаётся фоновой: связь восстанавливается тихо, а работа не прерывается.
 */
export const CONNECTION_MIN_FAILURES = 2
export const selectConnectionNeedsAttention = (state: ConnectionState): boolean =>
  state.status !== 'online' && state.failures >= CONNECTION_MIN_FAILURES && (state.userPending > 0 || state.userFailed)

export const selectConnectionOnline = (state: ConnectionState): boolean => state.status === 'online'
